import React from 'react';
import moment from 'moment';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { regionDateAndTime } from '../../utils';
import { useDriverSubscriptionGrid } from './hooks/DriverSubscriptionsGridHook';
import images from '../../assets/images';
import { ConfirmBox } from '../../components/confirm-box/ConfirmBox';
import Table from '../../components/table/Table';
import { IDriverSubscription } from '../../models/driver';
import { IDriverSubscriptionsProps } from '../../types/propTypes/propTypes';

const DriverPremiumSubscriptionsGrid = (props: IDriverSubscriptionsProps) => {
    const { t } = useTranslation();
    const filterState = props.filterState;

    const {
        subscriptionList,
        totalCount,
        modal,
        driverName,
        handleModal,
        cancelDriverSubscription,
        handlePageChange,
        handleSorting,
    } = useDriverSubscriptionGrid(props);

    const header = [
        {
            label: t('driverSubscriptions.label.name'),
            id: 'name',
            sortable: true,
            isVisible: true,
            cell: (row: IDriverSubscription) => {
                return (
                    <span className="d-block text-nowrap-normal" title={row.name}>
                        {row.name}
                    </span>
                );
            },
        },
        {
            label: t('driverSubscriptions.label.promotion'),
            id: 'promotion_name',
            sortable: true,
            isVisible: true,
            style: { maxWidth: 200 },
            cell: (row: IDriverSubscription) => {
                return (
                    <span className="d-block text-truncate" style={{ maxWidth: '100%' }} title={row.promotion_name}>
                        {row.promotion_name || '-'}
                    </span>
                );
            },
        },
        {
            label: t('driverSubscriptions.label.subscriptionType'),
            id: 'subscription_type',
            sortable: true,
            isVisible: true,
            cell: (row: IDriverSubscription) => {
                return (
                    <span
                        className="d-block text-nowrap-normal"
                        title={
                            row.is_subscription_paid
                                ? row.subscription_type
                                : t<string>('driverSubscriptions.label.freeTrial')
                        }
                    >
                        {row.is_subscription_paid ? row.subscription_type : t('driverSubscriptions.label.freeTrial')}
                    </span>
                );
            },
        },
        {
            label: t('driverSubscriptions.label.subscriptionStartDate'),
            id: 'start_date',
            sortable: true,
            isVisible: true,
            cell: (row: IDriverSubscription) => {
                if (row.start_date) {
                    return moment(row.start_date).format(regionDateAndTime().GENERAL_DATE_TIME_FORMAT);
                }
                return null;
            },
        },
        {
            label: t('driverSubscriptions.label.renewalOrExpiryDate'),
            id: 'end_date',
            sortable: true,
            isVisible: true,
            cell: (row: IDriverSubscription) => {
                if (row.end_date) {
                    return moment(row.end_date).format(regionDateAndTime().GENERAL_DATE_TIME_FORMAT);
                }
                return null;
            },
        },
        {
            label: t('driverSubscriptions.label.voltsCredited'),
            id: 'premium_subscription_volts_credited',
            sortable: true,
            isVisible: true,
            cell: (row: IDriverSubscription) => {
                return (
                    <span
                        className="d-block text-nowrap-normal"
                        title={String(row.premium_subscription_volts_credited || '')}
                    >
                        {row.premium_subscription_volts_credited || '-'}
                    </span>
                );
            },
        },
        {
            label: t('driverSubscriptions.label.journeysPlanned'),
            id: 'journeys_planned',
            sortable: true,
            isVisible: true,
            cell: (row: IDriverSubscription) => {
                return (
                    <span className="d-block text-nowrap-normal" title={String(row.journeys_planned || '')}>
                        {row.journeys_planned || '-'}
                    </span>
                );
            },
        },
        {
            label: t('driverSubscriptions.label.subscriptionStatus'),
            id: 'active',
            sortable: true,
            isVisible: true,
            className: 'text-center',
            cell: (row: IDriverSubscription) => {
                return (
                    <span className={row.active ? 'badge badge-success' : 'badge cancelled-color-code badge-secondary'}>
                        {row.active ? t('driverSubscriptions.label.active') : t('driverSubscriptions.label.cancelled')}
                    </span>
                );
            },
        },
        {
            label: t('driverSubscriptions.label.action'),
            id: 'action',
            sortable: false,
            isVisible: true,
            style: { width: 80 },
            cell: (row: IDriverSubscription) => {
                if (row.active) {
                    return (
                        <Dropdown className="actions">
                            <Dropdown.Toggle id="actions-dropdown">
                                <img
                                    title={t<string>('driverSubscriptions.label.action')}
                                    className="user-icon"
                                    src={images.dropdownMenuSVG}
                                    alt=""
                                />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {row.auto_renew && (
                                    <Dropdown.Item
                                        title={t<string>('driverSubscriptions.label.cancel')}
                                        onClick={() => {
                                            handleModal(
                                                t('driverSubscriptions.message.cancelConfirmMessage'),
                                                t('driverSubscriptions.label.cancelSubscription'),
                                                () => cancelDriverSubscription(row.id, false),
                                            );
                                        }}
                                    >
                                        <img
                                            src={images.disabledSVG}
                                            alt={t<string>('driverSubscriptions.label.cancel')}
                                        />
                                        {t('driverSubscriptions.label.cancel')}
                                    </Dropdown.Item>
                                )}
                                <Dropdown.Item
                                    title={t<string>('driverSubscriptions.label.terminate')}
                                    onClick={() => {
                                        const terminateMessage = row.auto_renew
                                            ? t('driverSubscriptions.message.terminateConfirmMessage')
                                            : t('driverSubscriptions.message.terminateConfirmMessageWithoutRenew');
                                        handleModal(
                                            terminateMessage,
                                            t('driverSubscriptions.label.terminateSubscription'),
                                            () => cancelDriverSubscription(row.id, true),
                                        );
                                    }}
                                >
                                    <img
                                        src={images.closeSVG}
                                        alt={t<string>('driverSubscriptions.label.terminate')}
                                        className="close-icon"
                                    />
                                    {t('driverSubscriptions.label.terminate')}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    );
                }
                return '';
            },
        },
    ];

    return (
        <>
            <div className="title row align-items-content justify-content-between pb-3">
                <div className="col-auto col-sm text-truncate pr-0">
                    <h1 className="text-truncate">
                        {driverName
                            ? `${t('driverSubscriptions.header')} - ${driverName}`
                            : t('driverSubscriptions.header')}
                    </h1>
                </div>
            </div>

            <div className="card-outer">
                <Table
                    columns={header}
                    data={subscriptionList}
                    rowsPerPage={filterState.rowsPerPage}
                    currentPage={filterState.currentPage}
                    totalCount={totalCount}
                    sortColumn={filterState.sortColumn}
                    sortOrder={filterState.sortOrder}
                    handlePageChange={handlePageChange}
                    handleSorting={handleSorting}
                    t={t}
                />
            </div>
            <ConfirmBox
                t={t}
                closeModel={handleModal}
                header={modal.header}
                body={modal.body}
                callBack={modal.callBack}
                showModel={modal.showModal}
            />
        </>
    );
};

export default DriverPremiumSubscriptionsGrid;
